import type { FieldDiff, FieldValueDiff } from "./medicalReview.types";
import { fieldLabel } from "./fieldLabels";

/** One field's old → new transition, with the new value emphasised. */
export function FieldValueChange({
  change,
  align = "left",
}: {
  change: FieldValueDiff | undefined;
  align?: "left" | "right";
}) {
  const hasOld = change?.old !== null && change?.old !== undefined;
  return (
    <span
      className={`inline-flex flex-wrap items-baseline gap-x-1.5 ${
        align === "right" ? "justify-end" : ""
      }`}
    >
      {hasOld && (
        <>
          <span className="text-[#8FA5A0] line-through">
            {format(change.old)}
          </span>
          <span className="text-[#8FA5A0]">→</span>
        </>
      )}
      <span
        className={`font-medium ${
          change?.material ? "text-[#A31D5C]" : "text-[#2B4752]"
        }`}
      >
        {format(change?.new)}
      </span>
    </span>
  );
}

export default function ChangeDiff({
  diff,
  compact = false,
}: {
  diff: FieldDiff;
  compact?: boolean;
}) {
  const entries = Object.entries(diff ?? {});
  if (entries.length === 0) return null;

  // Material changes lead: they are why the record came back for review.
  const ordered = [...entries].sort(
    ([, a], [, b]) =>
      Number(b?.material ?? false) - Number(a?.material ?? false),
  );
  const shown = compact ? ordered.slice(0, 3) : ordered;
  const hidden = ordered.length - shown.length;

  return (
    <div className="flex flex-col gap-1">
      {shown.map(([field, change]) => (
        <div
          key={field}
          className="flex flex-wrap items-baseline gap-x-2 text-xs"
        >
          <span className="text-[#556C75]">{fieldLabel(field)}:</span>
          <FieldValueChange change={change} />
        </div>
      ))}
      {hidden > 0 && (
        <span className="text-xs text-[#8FA5A0]">
          +{hidden} more {hidden === 1 ? "field" : "fields"}
        </span>
      )}
    </div>
  );
}

function format(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}
